// Busy.me's in-app notifications. This module is aliased over `vue3-toastify` (see vue.config.js),
// so EVERY `import { toast } from 'vue3-toastify'` in the app lands here unchanged — but instead of
// corner toasts, messages appear INSIDE the SmartBox (rendered by SmartBox.vue from `smartToasts`).
//
// It implements the toast API surface the app actually uses:
//   toast.loading(msg, opts) → id · toast.update(id, {render,isLoading,autoClose,theme,undo,view})
//   toast.done(id) · toast.success/error/info/warning(msg, opts) · toast.open({message,type,duration})
//   toast.remove(id) / toast.clearAll()

import { reactive } from 'vue'

// The live queue the SmartBox renders: [{ id, text, theme, loading, leaving }]
export const smartToasts = reactive([])

let seq = 0
const timers = new Map()

// theme → the module colour shown as the message's dot/accent (matches the app's module colours).
const THEME_COLORS = {
  // The mail notices ("Conversation archived." / "moved to trash." / "Marked as read.") sit on the
  // box's own blue at 8% (frames 40010468-188844 … 40010486-210692, Pouria 2026-09-21), not the
  // email module's teal.
  email: '#3741f3',
  task: '#84c867',
  event: '#737eb8',
  note: '#e4aa2c',
  contact: '#B87966',
  file: '#1BC09F',
  error: '#ef4444',
  success: '#22c55e',
  info: '#388bff',
  warning: '#e4aa2c',
  // The chat's "Possible connections" strips (frames 20/21, node 40012285:343526): the box blue
  // while a Connect is running — its 8% wash is the frame's #EEF0FF exactly — and the frame's deep
  // green once it landed, whose 8% wash is the frame's #EAF6EF. Their own themes rather than
  // `email`/`success`: `email` sits in the box's other seat (SmartToasts' `only="email"`) and
  // `success`'s green washes to a different plate than the one drawn.
  connect: '#3741f3',
  connected: '#00773e',
}
export const toastColor = (theme) => THEME_COLORS[theme] || '#6b7280'

/**
 * The wash a notification sits on (busy-my-web#194). Every notification in the app is the same
 * shape in the same place and differs only by type and colour, so the toast strip wears the notice
 * strip's own treatment: the theme colour at 8% behind it, the theme colour itself on the accent.
 * A per-theme colour cannot be a Tailwind class, so it is computed rather than looked up.
 */
export const toastTint = (theme, alpha = 0.08) => {
  const n = parseInt(toastColor(theme).slice(1), 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

// The message's own ink. Near-black everywhere except the landed connection, which the frame writes
// in its theme green (frame 21: tick, sentence and all).
const THEME_INK = { connected: '#00773e' }
export const toastInk = (theme) => THEME_INK[theme] || '#111827'

const find = (id) => smartToasts.find(t => t.id === id)

// Fade out (the UI animates `leaving`), then drop from the queue.
const dismiss = (id) => {
  const t = find(id)
  if (!t || t.leaving) return
  t.leaving = true
  setTimeout(() => {
    const i = smartToasts.findIndex(x => x.id === id)
    if (i !== -1) smartToasts.splice(i, 1)
  }, 350)
}

const scheduleDismiss = (id, delay) => {
  clearTimeout(timers.get(id))
  timers.set(id, setTimeout(() => dismiss(id), typeof delay === 'number' ? delay : 2500))
}

// An Undo on a notification (busy-my-web#303): `undo` is the callback the strip's Undo button
// runs; `undoLabel` is its wording. Only set when a caller offers one, so the queue entry of every
// other message is exactly what it was.
const setUndo = (t, { undo, undoLabel } = {}) => {
  if (typeof undo !== 'function') return
  t.undo = undo
  t.undoLabel = undoLabel || 'Undo'
}

// A View on a notification (chat Possible connections, frame 21): `view` is the callback the
// strip's View button runs — open what the message is about. Same rules as Undo: only set when a
// caller offers one.
const setView = (t, { view, viewLabel } = {}) => {
  if (typeof view !== 'function') return
  t.view = view
  t.viewLabel = viewLabel || 'View'
}

const push = (text, { theme, loading = false, autoClose = 2500, undo, undoLabel, view, viewLabel } = {}) => {
  const id = `st-${++seq}`
  // Keep the strip short — the oldest message yields.
  if (smartToasts.length >= 3) dismiss(smartToasts[0].id)
  const entry = { id, text: String(text ?? ''), theme: theme || 'info', loading, leaving: false }
  setUndo(entry, { undo, undoLabel })
  setView(entry, { view, viewLabel })
  smartToasts.push(entry)
  if (!loading) scheduleDismiss(id, autoClose)
  return id
}

function baseToast(msg, opts = {}) {
  return push(msg, {
    theme: opts.theme || opts.type, autoClose: opts.autoClose,
    undo: opts.undo, undoLabel: opts.undoLabel, view: opts.view, viewLabel: opts.viewLabel,
  })
}

baseToast.loading = (msg, opts = {}) => push(msg, { theme: opts.theme, loading: true })

baseToast.update = (id, opts = {}) => {
  const t = find(id)
  if (!t) return
  if (opts.render != null) t.text = String(opts.render)
  if (opts.theme) t.theme = opts.theme
  if (opts.type) t.theme = opts.type
  if (opts.isLoading === false) t.loading = false
  setUndo(t, opts)
  setView(t, opts)
  if (!t.loading) scheduleDismiss(id, typeof opts.autoClose === 'number' ? opts.autoClose : 2500)
}

// Take the action back: the notice closes at once (there is nothing left to undo twice) and the
// callback runs after. Answers whether there was anything to run.
baseToast.undo = (id) => {
  const t = find(id)
  if (!t || typeof t.undo !== 'function') return false
  const run = t.undo
  t.undo = null
  dismiss(id)
  run()
  return true
}

// Open what the notice is about: the notice closes (it has been answered) and the callback runs.
baseToast.view = (id) => {
  const t = find(id)
  if (!t || typeof t.view !== 'function') return false
  const run = t.view
  dismiss(id)
  run()
  return true
}

baseToast.done = (id) => {
  const t = find(id)
  if (!t) return
  if (t.loading) {
    // A loading toast that was never updated — close it out quietly.
    t.loading = false
    scheduleDismiss(id, 1200)
  }
}

baseToast.success = (msg, opts = {}) => push(msg, { theme: opts.theme || 'success', autoClose: opts.autoClose })
baseToast.error = (msg, opts = {}) => push(msg, { theme: opts.theme || 'error', autoClose: opts.autoClose })
baseToast.info = (msg, opts = {}) => push(msg, { theme: opts.theme || 'info', autoClose: opts.autoClose })
baseToast.warning = (msg, opts = {}) => push(msg, { theme: opts.theme || 'warning', autoClose: opts.autoClose })
// Legacy vue-toastification-style calls: toast.open({ message, type, duration })
baseToast.open = (opts = {}) => push(opts.message || '', { theme: opts.type, autoClose: opts.duration })
baseToast.remove = (id) => dismiss(id)
baseToast.clearAll = () => [...smartToasts].forEach(t => dismiss(t.id))
// vue3-toastify extras some code may poke — harmless no-ops.
baseToast.dismiss = (id) => dismiss(id)
baseToast.isActive = (id) => !!find(id)

export const toast = baseToast

// `app.use(Vue3Toastify, {...})` in main.js — nothing to install anymore.
export default { install() { /* corner toasts replaced by the SmartBox strip */ } }
